import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './context/AuthContext';
import DocumentTitle from './components/layout/DocumentTitle';
import { AudioPlayerProvider } from './context/AudioPlayerContext';

// Layouts
import MainLayout from './components/layout/MainLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import BooksManagement from './pages/BooksManagement';
import CategoriesManagement from './pages/CategoriesManagement';
import CollectionsManagement from './pages/CollectionsManagement';
import UsersManagement from './pages/UsersManagement';
import Profile from './pages/Profile';
import NotFound from './pages/NotFound';
import BookDetail from './pages/BookDetail';
import CollectionDetail from './pages/CollectionDetail';
import Settings from './pages/Settings';
import BookOfTheDayManagement from './pages/BookOfTheDayManagement';
import JobsManagement from './pages/JobsManagement';
import AdminsManagement from './pages/AdminsManagement';
import AudioFilesManagement from './pages/AudioFilesManagement';
import CourseManagement from './pages/CourseManagement';
import CourseDetail from './pages/CourseDetail'; // Import the new page

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#3f51b5',
    },
    secondary: {
      main: '#f50057',
    },
    background: {
      default: '#f5f5f9',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  return children;
};

// App with AuthProvider wrapper
const AppWithAuth = () => {
  return (
    <Router>
      {/* Add DocumentTitle component to update title based on route */}
      <DocumentTitle />
      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* Protected routes with MainLayout */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="books" element={<BooksManagement />} />
          <Route path="books/:bookId" element={<BookDetail />} />
          <Route path="categories" element={<CategoriesManagement />} />
          <Route path="collections" element={<CollectionsManagement />} />
          <Route path="collections/:collectionId" element={<CollectionDetail />} />
          <Route path="book-of-the-day" element={<BookOfTheDayManagement />} />
          <Route path="jobs" element={<JobsManagement />} />
          <Route path="users" element={<UsersManagement />} />
          <Route path="profile" element={<Profile />} />
          <Route path="settings" element={<Settings />} />
          <Route path="admins" element={<AdminsManagement />} />
          <Route path="audio-files" element={<AudioFilesManagement />} />
          <Route path="courses" element={<CourseManagement />} />
          <Route path="courses/:courseId" element={<CourseDetail />} /> {/* Add the new route */}
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AudioPlayerProvider>
          <AppWithAuth />
        </AudioPlayerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
